import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, CssBaseline } from '@mui/material';
import { useRouter } from 'next/router';

import {
  useAssignees,
  useConsolidateStoreOperations,
  useDevice,
  useServicePoints,
} from '@hooks';

import theme from 'config/theme';
import Main from './Main';
import Drawer from './Drawer';
import styles from './drawer.style';
import { TicketDrawer } from './TicketDrawer';

const DrawerLayout = ({ children }) => {
  useConsolidateStoreOperations(true);
  const { isReady, pathname } = useRouter();
  const isServicePointsPage = pathname?.startsWith('/servicePoints');
  const isLoginPage = pathname?.startsWith('/login');

  const enabled = isReady && !!pathname && !isLoginPage;
  const servicePointsEnabled = enabled && !isServicePointsPage;

  // Prefetch data hooks
  useAssignees({ enabled });
  useServicePoints({ enabled: servicePointsEnabled });

  const drawerOpen = useRef(true);
  const ticketCreationDrawerOpen = useRef(true);

  const { isMobile, isTablet } = useDevice();

  const isSmallScreen = isMobile || isTablet;

  const [open, setOpen] = useState(!isSmallScreen);
  const [ticketOpen, setTicketOpen] = useState(false);

  useEffect(() => {
    if (isSmallScreen) {
      drawerOpen.current = open;
      if (open) {
        setOpen(false);
      }
    } else {
      setOpen(drawerOpen.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmallScreen]);

  const handleDrawerToggle = useCallback(() => {
    setOpen((prev) => {
      const isDrawerOpen = !prev;

      if (!isSmallScreen) {
        drawerOpen.current = isDrawerOpen;
      }
      return isDrawerOpen;
    });
  }, [isSmallScreen]);

  const handleTicketDrawerToggle = useCallback(() => {
    setTicketOpen((prev) => {
      const isDrawerOpen = !prev;

      if (!isSmallScreen) {
        ticketCreationDrawerOpen.current = isDrawerOpen;
      }
      return isDrawerOpen;
    });
  }, [isSmallScreen]);

  return (
    <Box sx={styles.wrapper}>
      <CssBaseline />
      <Drawer onToggle={handleDrawerToggle} open={open} />
      <Main open={open} onToggleDrawer={handleDrawerToggle}>
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              width: ticketOpen
                ? `calc(100% - ${theme.customValues.ticketCreationDrawerWidth})`
                : `calc(100% - ${theme.customValues.closedDrawerWidth})`,
              maxWidth: ticketOpen
                ? `calc(100% - ${theme.customValues.ticketCreationDrawerWidth})`
                : '100%',
            }}
          >
            {children}
          </Box>

          <TicketDrawer open={ticketOpen} onToggle={handleTicketDrawerToggle} />
        </Box>
      </Main>
    </Box>
  );
};

const memoizedDrawerLayout = React.memo(DrawerLayout);
export { memoizedDrawerLayout as DrawerLayout };
